// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { benefitBucketsStyles } from "@turnerBlueprint/App/Widgets/BenefitBuckets/BenefitBuckets";
import { color, xScreen } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

benefitBucketsStyles.container = {
    css: css`
        padding-top: 0;
        display: grid;
        @media print {
            page-break-inside: avoid;
        }

        ${({ numberOfCards }: any) =>
            numberOfCards &&
            css`
                grid-template-columns: repeat(${numberOfCards}, 1fr);
            `};

        ${({ gapBetweenCards }: any) =>
            gapBetweenCards &&
            css`
                gap: ${gapBetweenCards}px;
            `};

        ${({ tabletGapBetweenCards }: any) =>
            tabletGapBetweenCards &&
            css`
                ${xScreen("lte", "md")} {
                    gap: ${tabletGapBetweenCards}px;
                }
            `};

        ${xScreen("lt", "sm")} {
            grid-template-columns: 1fr;
            ${({ mobileGapBetweenCards }: any) =>
                mobileGapBetweenCards &&
                css`
                    gap: ${mobileGapBetweenCards}px;
                `};
        }
    `,
};
benefitBucketsStyles.content = {
    css: css`
        display: flex;
        background-color: ${color("common.white")};

        ${({ showBorder }: any) =>
            showBorder &&
            css`
                border: 1px solid ${color("common.border")};
                box-shadow: 0 2px 0 0 ${color("common.ui4")};
                padding: 16px;
            `};

        ${({ imagePosition }: any) =>
            css`
                flex-direction: ${imagePosition};
            `};

        ${({ cardContentGap }: any) => {
            return css`
                gap: ${cardContentGap}px;
            `;
        }}

        ${xScreen("lte", "md")} {
            ${({ tabletImagePosition }: any) =>
                tabletImagePosition !== "hide" &&
                css`
                    flex-direction: ${tabletImagePosition};
                `};
        }

        ${xScreen("lt", "sm")} {
            width: 100%;

            ${({ mobileImagePosition }: any) =>
                mobileImagePosition !== "hide" &&
                css`
                    flex-direction: ${mobileImagePosition};
                `};
            ${({ mobileCardContentGap }: any) => {
                return css`
                    gap: ${mobileCardContentGap}px;
                `;
            }}
        }

        @media print {
            page-break-inside: avoid;
        }
    `,
};

benefitBucketsStyles.imageWrapper = {
    css: css`
        flex: 0 0 auto;
        max-height: 100%;
        overflow: hidden;
        margin: auto;

        ${({ imageHeight }: any) =>
            imageHeight &&
            css`
                height: ${imageHeight}px;
            `};

        ${({ mobileImagePosition }: any) =>
            mobileImagePosition === "hide" &&
            css`
                ${xScreen("lte", "md")} {
                    display: none;
                }
            `};

        ${({ tabletImagePosition }: any) =>
            tabletImagePosition === "hide" &&
            css`
                ${xScreen("lt", "sm")} {
                    display: none;
                }
            `};
    `,
};
benefitBucketsStyles.image = {
    css: css`
        width: 100%;
        height: 100%;
        img {
            width: auto;
            height: 100%;
            max-width: 100%;
            max-height: 100%;
            object-fit: cover;
        }
    `,
};

benefitBucketsStyles.cardContentWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        flex: 1;

        ${({ contentAligment }: any) => {
            if (contentAligment === "right") {
                return css`
                    align-items: flex-end;
                `;
            }
            if (contentAligment === "left") {
                return css`
                    align-items: flex-start;
                `;
            }
            return css`
                align-items: center;
                text-align: center;
            `;
        }};
        ${xScreen("lte", "md")} {
            ${({ tabletContentAligment }: any) => {
                if (tabletContentAligment === "right") {
                    return css`
                        align-items: flex-end;
                        text-align: right;
                    `;
                }
                if (tabletContentAligment === "left") {
                    return css`
                        align-items: flex-start;
                        text-align: left;
                    `;
                }
                return css`
                    align-items: center;
                    text-align: center;
                `;
            }};
        }
        ${xScreen("lt", "sm")} {
            ${({ mobileContentAligment }: any) => {
                if (mobileContentAligment === "right") {
                    return css`
                        align-items: flex-end;
                        text-align: right;
                    `;
                }
                if (mobileContentAligment === "left") {
                    return css`
                        align-items: flex-start;
                        text-align: left;
                    `;
                }
                return css`
                    align-items: center;
                    text-align: center;
                `;
            }};
        }
    `,
};
benefitBucketsStyles.title = {
    forwardAs: "h2",
    variant: "h5",
    css: css`
        margin-bottom: 8px;
    `,
};
benefitBucketsStyles.subtitle = {
    variant: "p",
    size: "14px",
    css: css`
        line-height: 22px;
        margin-bottom: 0;
    `,
};
